// ./src/utils/api/NoteService.ts
//
// Note (free-form event / account notes) service. OpResult-native. On the event
// Notes page every list is filtered by eventId. A note carries an optional title
// + message (markdown or rich text), a private flag, and a set of tags (reusing
// the account tag catalog). Full single-item CRUD plus a bulk status change /
// remove (array of ids in the where clause). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { Tag } from "./TagService";

// =============================================================================
// GraphQL
// =============================================================================

const NOTE_FIELDS = `
  id
  eventId
  accountId
  title
  message
  messageType
  isPrivate
  tags { id name color }
  rights
  createdBy
  createdByUser { id firstName lastName username avatarUrl }
  createdAt
  updatedAt
`;

const NOTE_LIST_QUERY = gql`
  query NoteList($filter: NoteFilter, $params: PaginationAndSorting) {
    noteList(filter: $filter, params: $params) {
      code
      data { ${NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const NOTE_GET_QUERY = gql`
  query NoteGet($id: ID) {
    noteGet(id: $id) {
      code
      data { ${NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const NOTE_GET_MANY_QUERY = gql`
  query NoteGetMany($ids: [ID]) {
    noteGetMany(ids: $ids) {
      code
      data { ${NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const NOTE_CREATE_MUTATION = gql`
  mutation NoteCreate($params: NoteInput) {
    noteCreate(params: $params) {
      code
      data { ${NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const NOTE_UPDATE_MUTATION = gql`
  mutation NoteUpdate($where: NoteWhereInput, $params: NoteInput) {
    noteUpdate(where: $where, params: $params) {
      code
      data { ${NOTE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const NOTE_REMOVE_MUTATION = gql`
  mutation NoteRemove($where: NoteWhereInput) {
    noteRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Note schema)
// =============================================================================

/** How a note's message body is authored. */
export const NOTE_MESSAGE_TYPE = {
  RICH_TEXT: "rich_text",
  MARKDOWN: "markdown",
} as const;

export type NoteMessageType = (typeof NOTE_MESSAGE_TYPE)[keyof typeof NOTE_MESSAGE_TYPE];

export interface Note {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  title?: string | null;
  message?: string | null;
  messageType?: string | null;
  isPrivate?: boolean | null;
  /** Tags attached to this note (from the account tag catalog). */
  tags?: Tag[] | null;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdBy?: string | null;
  /** The creating user (name/avatar), resolved from ms_auth — for the "added by … when" line. */
  createdByUser?: NoteAuthor | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Minimal identity of a note's creator — enough for the list's "added by" line. */
export interface NoteAuthor {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
}

export interface NoteInput {
  eventId?: string | null;
  title?: string | null;
  message?: string | null;
  messageType?: string | null;
  isPrivate?: boolean | null;
  /** Replaces the whole tag set. [] detaches all; omit to leave tags unchanged. */
  tagIds?: string[] | null;
}

export interface NoteFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  /** Keep only notes carrying ANY of these tags. */
  tagIds?: string[] | null;
  searchKeyword?: string | null;
}

export interface NoteWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class NoteService {
  static async list(filter?: NoteFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(NOTE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(NOTE_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(NOTE_GET_MANY_QUERY, { ids });
  }

  static async create(params: NoteInput): Promise<OpResult> {
    return ApiService.executeMutation(NOTE_CREATE_MUTATION, { params });
  }

  static async update(where: NoteWhereInput, params: NoteInput): Promise<OpResult> {
    return ApiService.executeMutation(NOTE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(NOTE_REMOVE_MUTATION, { where: { id } });
  }

  /** Remove every selected note in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(NOTE_REMOVE_MUTATION, { where: { id: ids } });
  }
}
